import { SubmissionError } from "redux-form";
import axios from "axios";

import { Snackbar } from "@js/components/snackbar";
import type { AppThunkAction } from "@js/store";
import type { PaginatedResult } from "@js/types/generic";
import type { FreelancerInvoice } from "@js/types/invoices";
import { serializeArrayParams } from "@js/utils";

import * as actionTypes from "./action-types";
import type { CreateFreelancerInvoiceValues } from "./types";

type EditFreelancerInvoiceValues = Omit<
  CreateFreelancerInvoiceValues,
  "is_expense" | "employer"
>;

export const editFreelancerInvoice =
  (
    id: number,
    values: EditFreelancerInvoiceValues,
  ): AppThunkAction<Promise<void>> =>
  () =>
    new Promise((resolve, reject) =>
      axios
        .put(`/api/freelancer_invoices/${id}/`, values)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => reject(new SubmissionError(error.response.data))),
    );

export const lockInvoice = (
  id: number,
): Promise<{ payment_blocked_until: string }> =>
  new Promise((resolve, reject) =>
    axios
      .post(`/api/freelancer_invoices/${id}/block_payment_for_editing/`, {})
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => reject(error.response.data)),
  );

export const fetchFreelancerInvoicesSummary =
  (): AppThunkAction<Promise<void>> => (dispatch) =>
    new Promise((resolve) => {
      return axios
        .get(`/api/invoice_statistics/freelancer/`)
        .then((response) => {
          dispatch({
            type: actionTypes.FETCH_FREELANCER_INVOICES_SUMMARY_SUCCESS,
            payload: response.data,
          });

          resolve(response.data);
        })
        .catch(() =>
          dispatch({
            type: actionTypes.FETCH_FREELANCER_INVOICES_SUMMARY_FAILED,
          }),
        );
    });

type GetFreelancerInvoicesParams = {
  job?: number;
  paid?: boolean;
  unpaid?: boolean;
  ordering?:
    "employer_name" | "number" | "issued_at" | "due_date" | "_gross_total";
  page?: number;
  search?: string;
};

export const fetchFreelancerInvoices =
  (
    params: GetFreelancerInvoicesParams,
  ): AppThunkAction<Promise<PaginatedResult<FreelancerInvoice>>> =>
  (dispatch) =>
    new Promise((resolve) => {
      dispatch({
        type: actionTypes.FETCH_FREELANCER_INVOICES,
      });

      return axios
        .get(`/api/freelancer_invoices/`, { params })
        .then((response) => {
          dispatch({
            type: actionTypes.FETCH_FREELANCER_INVOICES_SUCCESS,
            payload: response.data,
          });

          resolve(response.data);
        })
        .catch(() =>
          dispatch({
            type: actionTypes.FETCH_FREELANCER_INVOICES_FAILED,
          }),
        );
    });

const genericFetchEmployerInvoices =
  (
    fetchActionType: string,
    fetchSuccessActionType: string,
    fetchFailedActionType: string,
  ) =>
  (params: { ids?: string }): AppThunkAction<Promise<void>> =>
  (dispatch) =>
    new Promise((resolve) => {
      dispatch({
        type: fetchActionType,
      });

      return axios
        .get(`/api/employer_invoices/`, { params })
        .then((response) => {
          dispatch({
            type: fetchSuccessActionType,
            payload: response.data,
          });

          resolve(response.data);
        })
        .catch(() =>
          dispatch({
            type: fetchFailedActionType,
          }),
        );
    });

export const fetchDependentEmployerInvoices = genericFetchEmployerInvoices(
  actionTypes.FETCH_DEPENDENT_EMPLOYER_INVOICES,
  actionTypes.FETCH_DEPENDENT_EMPLOYER_INVOICES_SUCCESS,
  actionTypes.FETCH_DEPENDENT_EMPLOYER_INVOICES_FAILED,
);

export const cancelFreelancerInvoice =
  (invoiceId: FreelancerInvoice["id"]): AppThunkAction<Promise<void>> =>
  () =>
    new Promise((resolve, reject) =>
      axios
        .post(`/api/freelancer_invoices/${invoiceId}/cancel/`)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => reject(error.response.data)),
    );

export const fetchFreelancerLastInvoice =
  (): AppThunkAction<Promise<void>> => (dispatch) =>
    new Promise((resolve) => {
      dispatch({
        type: actionTypes.FETCH_FREELANCER_LAST_INVOICE,
      });

      return axios
        .get("/api/freelancer_invoices/?ordering=-created&page=1")
        .then((response) => {
          const data = response.data;
          let lastInvoice = null;

          if (data.results && data.results?.length > 0) {
            lastInvoice = data.results[0];
          }

          dispatch({
            type: actionTypes.FETCH_FREELANCER_LAST_INVOICE_SUCCESS,
            payload: lastInvoice,
          });
          resolve(lastInvoice);
        })
        .catch(() =>
          dispatch({
            type: actionTypes.FETCH_FREELANCER_LAST_INVOICE_FAILED,
          }),
        );
    });

export const downloadInvoicesPDF = (params?: { ids: number[] }) =>
  axios
    .get("/api/employer_invoices/download_zip/", {
      responseType: "blob",
      params,
      paramsSerializer: serializeArrayParams,
    })
    .then((response) => {
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "invoices.zip");
      document.body.appendChild(link);
      link.click();
      link.remove();
    })
    .catch(() =>
      Snackbar.error(
        "Please, narrow your search criteria to enable PDF download.",
      ),
    );
